import React, { useMemo } from 'react';
import {
  Search,
  X,
  ArrowUp,
  ArrowDown,
  ArrowUpDown,
  Printer,
  FileText,
  FileSpreadsheet,
} from 'lucide-react';

export const MEMBER_SORT_OPTIONS = [
  { value: 'name', label: 'Name' },
  { value: 'age', label: 'Age' },
  { value: 'district', label: 'District' },
  { value: 'samithi', label: 'Samithi' },
  { value: 'sector', label: 'Sector' },
];

export const SEVADAL_SORT_OPTIONS = [
  { value: 'name', label: 'Name' },
  { value: 'district', label: 'District' },
  { value: 'samithi', label: 'Samithi' },
  { value: 'sector', label: 'Sector' },
];

export const MEMBER_SEARCH_FIELDS = [
  'name',
  'age',
  'bloodgroup',
  'district',
  'samithi',
  'mobile',
  'saiconnectedid',
  'aadhar',
  'certificateno',
  'sector',
  'deploymentpoint',
  'reason',
  'removedOn',
  'address',
];

export const SEVADAL_SEARCH_FIELDS = [
  'name',
  'age',
  'bloodgroup',
  'district',
  'samithi',
  'mobile',
  'saiconnectedid',
  'sector',
  'deploymentpoint',
];

export const reportTableStyles = {
  headRow: {
    style: {
      backgroundColor: '#f8fafc',
      borderBottomWidth: '1px',
      borderBottomColor: '#e2e8f0',
      minHeight: '56px',
    },
  },
  headCells: {
    style: {
      whiteSpace: 'normal',
      wordBreak: 'break-word',
      overflowWrap: 'break-word',
      lineHeight: '1.4',
      minHeight: '56px',
      paddingTop: '10px',
      paddingBottom: '10px',
      fontSize: '13px',
    },
  },
  cells: {
    style: {
      whiteSpace: 'normal',
      wordBreak: 'break-word',
      overflowWrap: 'break-word',
      lineHeight: '1.4',
      minHeight: '64px',
      paddingTop: '8px',
      paddingBottom: '8px',
    },
  },
  rows: {
    style: {
      borderBottomColor: '#f1f5f9',
      '&:hover': {
        backgroundColor: '#f8fafc',
      },
    },
  },
};

export function filterAndSortRows(
  rows = [],
  {
    searchTerm = '',
    searchableFields = [],
    sortBy,
    sortDirection = 'asc',
    numericFields = ['age', 'registered', 'required', 'needed'],
  } = {}
) {
  const query = searchTerm.trim().toLowerCase();

  let filtered = rows;
  if (query) {
    filtered = rows.filter((row) =>
      searchableFields.some((field) => {
        const value = row[field];
        return value != null && String(value).toLowerCase().includes(query);
      })
    );
  }

  if (!sortBy) return filtered;

  return [...filtered].sort((a, b) => {
    const aVal = a[sortBy];
    const bVal = b[sortBy];

    if (numericFields.includes(sortBy)) {
      const aNum = Number(aVal) || 0;
      const bNum = Number(bVal) || 0;
      return sortDirection === 'asc' ? aNum - bNum : bNum - aNum;
    }

    const aStr = (aVal ?? '').toString().toLowerCase();
    const bStr = (bVal ?? '').toString().toLowerCase();
    if (aStr < bStr) return sortDirection === 'asc' ? -1 : 1;
    if (aStr > bStr) return sortDirection === 'asc' ? 1 : -1;
    return 0;
  });
}

export function useFilteredSortedData(rows, options) {
  const {
    searchTerm,
    searchableFields,
    sortBy,
    sortDirection,
    numericFields,
  } = options;

  return useMemo(
    () =>
      filterAndSortRows(rows, {
        searchTerm,
        searchableFields,
        sortBy,
        sortDirection,
        numericFields,
      }),
    [rows, searchTerm, searchableFields, sortBy, sortDirection, numericFields]
  );
}

export function SortHeader({ label, field, sortBy, sortDirection, onSort }) {
  const isActive = sortBy === field;

  return (
    <button
      type="button"
      onClick={() => onSort(field)}
      className={`inline-flex items-center gap-1.5 font-semibold transition-colors ${
        isActive ? 'text-primary' : 'text-gray-700 hover:text-primary'
      }`}
    >
      <span>{label}</span>
      {isActive ? (
        sortDirection === 'asc' ? (
          <ArrowUp size={14} className="shrink-0 text-primary" />
        ) : (
          <ArrowDown size={14} className="shrink-0 text-primary" />
        )
      ) : (
        <ArrowUpDown size={14} className="shrink-0 text-gray-400" />
      )}
    </button>
  );
}

export function makeSortableColumn(label, field, sortBy, sortDirection, onSort) {
  return {
    name: (
      <SortHeader
        label={label}
        field={field}
        sortBy={sortBy}
        sortDirection={sortDirection}
        onSort={onSort}
      />
    ),
    selector: (row) => row[field],
    sortable: false,
    cell: (row) => <div data-title={label}>{row[field]}</div>,
  };
}

export function makePlainColumn(label, field, options = {}) {
  return {
    name: label,
    selector: (row) => row[field],
    sortable: false,
    cell: (row) => <div data-title={label}>{row[field]}</div>,
    ...options,
  };
}

export function ReportTableControls({
  searchTerm,
  onSearchChange,
  searchPlaceholder = 'Search table...',
  sortBy,
  onSortByChange,
  sortDirection,
  onSortDirectionChange,
  sortOptions,
  totalCount,
  filteredCount,
  actions,
  hint,
}) {
  const showSort = Array.isArray(sortOptions) && sortOptions.length > 0;

  return (
    <div className="rounded-xl border border-slate-200 bg-white shadow-sm mb-4 overflow-hidden">
      <div className="flex flex-col lg:flex-row lg:items-end gap-3 p-4 bg-slate-50/80">
        <div className="relative flex-1 max-w-md w-full">
          <label htmlFor="report-table-search" className="block text-xs font-medium text-slate-600 mb-1.5">
            Search
          </label>
          <div className="relative">
            <Search
              size={16}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"
            />
            <input
              id="report-table-search"
              type="search"
              placeholder={searchPlaceholder}
              value={searchTerm}
              onChange={(e) => onSearchChange(e.target.value)}
              className="w-full rounded-lg border border-slate-200 bg-white pl-9 pr-9 py-2.5 text-sm text-slate-800 placeholder:text-slate-400 shadow-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
            />
            {searchTerm && (
              <button
                type="button"
                onClick={() => onSearchChange('')}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 p-1 rounded-md text-slate-400 hover:text-slate-600 hover:bg-slate-100"
                aria-label="Clear search"
              >
                <X size={14} />
              </button>
            )}
          </div>
        </div>

        {showSort && (
          <div className="flex flex-wrap items-end gap-3">
            <div>
              <label htmlFor="report-sort-by" className="block text-xs font-medium text-slate-600 mb-1.5">
                Sort by
              </label>
              <select
                id="report-sort-by"
                value={sortBy}
                onChange={(e) => onSortByChange(e.target.value)}
                className="rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-800 shadow-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary min-w-[140px]"
              >
                {sortOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label htmlFor="report-sort-dir" className="block text-xs font-medium text-slate-600 mb-1.5">
                Order
              </label>
              <select
                id="report-sort-dir"
                value={sortDirection}
                onChange={(e) => onSortDirectionChange(e.target.value)}
                className="rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-800 shadow-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary min-w-[140px]"
              >
                <option value="asc">Ascending</option>
                <option value="desc">Descending</option>
              </select>
            </div>
          </div>
        )}

        <div className="flex flex-wrap items-center gap-2 lg:ml-auto lg:pb-0.5">
          {typeof filteredCount === 'number' && (
            <span className="inline-flex items-center rounded-full bg-primary/10 text-primary px-3 py-1.5 text-xs font-semibold">
              {filteredCount}
              {typeof totalCount === 'number' && searchTerm.trim() && totalCount !== filteredCount
                ? ` of ${totalCount}`
                : ''}{' '}
              rows
            </span>
          )}
          {actions}
        </div>
      </div>

      {hint && (
        <div className="px-4 py-2 text-xs text-slate-500 border-t border-slate-100">
          {hint}
        </div>
      )}
    </div>
  );
}

export function ExportButtons({ onPrint, onPdf, onXls, show }) {
  if (!show) return null;

  return (
    <div className="flex items-center gap-2">
      <button
        type="button"
        onClick={onPrint}
        className="bg-primary text-white px-3 py-2 rounded-lg inline-flex items-center gap-1.5 text-xs font-medium hover:opacity-90 transition-opacity"
      >
        <Printer size={15} className="min-w-4" />
        <span className="hidden sm:inline">Print</span>
      </button>
      <button
        type="button"
        onClick={onPdf}
        className="bg-blue-600 text-white px-3 py-2 rounded-lg inline-flex items-center gap-1.5 text-xs font-medium hover:bg-blue-700 transition-colors"
      >
        <FileText size={15} className="min-w-4" />
        <span className="hidden sm:inline">PDF</span>
      </button>
      <button
        type="button"
        onClick={onXls}
        className="bg-emerald-600 text-white px-3 py-2 rounded-lg inline-flex items-center gap-1.5 text-xs font-medium hover:bg-emerald-700 transition-colors"
      >
        <FileSpreadsheet size={15} className="min-w-4" />
        <span className="hidden sm:inline">XLS</span>
      </button>
    </div>
  );
}

export function createColumnSortHandler(sortBy, setSortBy, sortDirection, setSortDirection) {
  return (field) => {
    if (sortBy === field) {
      setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(field);
      setSortDirection('asc');
    }
  };
}
